import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NewRequestWhere } from '@/domain/newRequest';
import { AbsenceWithTypeData } from '@/domain/requestsPage';
import AdminRequestDetailsModal from '@/pages/Requests/RequestsContent/RequestDetailsModal/RequestDetailsModal';
import useLeadRequestsLogic from '@/pages/Requests/RequestsContent/useRequestsLogic';
import { REQUEST_STATUS } from '@/utils/enums/requestsPage';

import { RangeCalendarDate } from '../types';

interface CalendarUpdateCallbacks {
    getRequestsTypes: () => void;
    refetchUsers: (forceRefresh?: boolean) => void;
}

interface RequestDetailsOptions {
    handleSetSelectedAbsenceId: (absenceId: string | null) => void;
    registerUpdateCallbacks: (callbacks: CalendarUpdateCallbacks) => void;
    triggerCalendarUpdate: () => void;
    handleRangeDate: (rangeDate: RangeCalendarDate) => void;
    rangeDates: RangeCalendarDate | undefined;
}

export const RequestDetailsAcceptanceContext = createContext<RequestDetailsOptions>({
    handleSetSelectedAbsenceId: () => null,
    registerUpdateCallbacks: () => null,
    triggerCalendarUpdate: () => null,
    handleRangeDate: () => null,
    rangeDates: undefined,
});

interface RequestDetailsAcceptanceProviderProps {
    children: ReactNode;
}

export const RequestDetailsAcceptanceProvider = ({ children }: RequestDetailsAcceptanceProviderProps) => {
    const calendarUpdateCallbacksRef = useRef<CalendarUpdateCallbacks | null>(null);
    const getRequestsRef = useRef<(() => void) | null>(null);

    const [selectedAbsenceId, setSelectedAbsenceId] = useState<string>();
    const [rangeDates, setRangeDates] = useState<RangeCalendarDate>();
    const [selectedRequestDetails, setSelectedRequestDetails] = useState<AbsenceWithTypeData>();

    const handleRangeDate = useCallback((rangeDate: RangeCalendarDate) => {
        setRangeDates(rangeDate);
    }, []);

    const handleSetSelectedAbsenceId = useCallback((id: string | null) => {
        setSelectedAbsenceId(id ?? undefined);
    }, []);

    const handleSelectedRequestDetails = useCallback((requestDetails: AbsenceWithTypeData | undefined) => {
        setSelectedRequestDetails(requestDetails);
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedRequestDetails(undefined);
    }, []);

    const triggerCalendarUpdate = useCallback(() => {
        if (calendarUpdateCallbacksRef.current) {
            calendarUpdateCallbacksRef.current.getRequestsTypes();
            calendarUpdateCallbacksRef.current.refetchUsers(true);
            handleCloseModal();
        }
        getRequestsRef.current?.();
    }, [handleCloseModal]);

    const { requestsList, isLoading, getRequests, openDetailsModal, onUpdateAbsenceStatus } = useLeadRequestsLogic({
        isCalendar: true,
        rangeDates,
        onUpdateSuccessCallback: triggerCalendarUpdate,
    });

    getRequestsRef.current = getRequests;

    const registerUpdateCallbacks = useCallback((callbacks: CalendarUpdateCallbacks) => {
        calendarUpdateCallbacksRef.current = callbacks;
    }, []);

    const getSelectedRequest = useCallback(
        (selectedAbsenceId: string) => {
            return requestsList?.elements.find(request => request._id === selectedAbsenceId);
        },
        [requestsList]
    );

    useEffect(() => {
        if (!selectedAbsenceId) return;
        const selectedRequest = getSelectedRequest(selectedAbsenceId);

        if (selectedRequest) {
            handleSelectedRequestDetails(selectedRequest);
        }
    }, [selectedAbsenceId, requestsList, openDetailsModal]);

    useEffect(() => {
        if (!selectedRequestDetails) {
            setSelectedAbsenceId(undefined);
        }
    }, [selectedRequestDetails]);

    const updateRequestAbsence = useCallback(
        (absenceId: string, status: REQUEST_STATUS, name: string | undefined, isDeletionRequest: boolean) =>
            (event: React.BaseSyntheticEvent) => {
                onUpdateAbsenceStatus(absenceId, status, name, isDeletionRequest, NewRequestWhere.webSchedule)(event);
            },
        [onUpdateAbsenceStatus]
    );

    const contextValue = useMemo(
        () => ({
            handleSetSelectedAbsenceId,
            registerUpdateCallbacks,
            triggerCalendarUpdate,
            handleRangeDate,
            rangeDates,
        }),
        [handleSetSelectedAbsenceId, registerUpdateCallbacks, triggerCalendarUpdate, handleRangeDate, rangeDates]
    );

    return (
        <RequestDetailsAcceptanceContext.Provider value={contextValue}>
            {children}
            {selectedRequestDetails && (
                <AdminRequestDetailsModal
                    singleRequestDetails={selectedRequestDetails}
                    isModalOpen={true}
                    isLoading={isLoading}
                    onCloseModal={handleCloseModal}
                    onUpdateAbsenceStatus={updateRequestAbsence}
                    getRequests={getRequests}
                    onEditRequest={triggerCalendarUpdate}
                    where={NewRequestWhere.webSchedule}
                />
            )}
        </RequestDetailsAcceptanceContext.Provider>
    );
};
